import React, { useState } from 'react';
import BrandHeader from '../../../components/common/BrandHeader';
import ActionBar from '../../../components/common/ActionBar';
import ContactHelpText from '../../../components/common/ContactHelpText';
import InlineAlert from '../../../components/common/InlineAlert';
import {
  Card,
  CardBody,
  PageHead,
  PageShell,
  PageSubtitle,
  PageTitle,
} from '../../../components/common/Card';
import { CONTACT_PHONE } from '../../../constants/business';
import { TIME_FORMAT } from '../../../constants/time';
import { verifyDateOfBirth } from '../../../API/workflow';
import { savePatientAccessToken } from '../../../utils/patientAccessStorage';
import { DateInput, Field, FieldLabel, RequiredMark } from './styles';

const DATE_OF_BIRTH_PATTERN = 'YYYY-MM-DD';

const MISMATCH_MESSAGE = 'Date of birth does not match.';
const MISSING_INPUT_MESSAGE = 'Please enter your date of birth.';
const LOCKED_MESSAGE = `Too many incorrect attempts. Please try again in 15 minutes or call ${CONTACT_PHONE} to speak with us.`;
const UNAVAILABLE_MESSAGE = `We could not load your appointment details. Please call ${CONTACT_PHONE} to speak with us.`;

const VERIFICATION_RESULT = {
  VERIFIED: 'verified',
  MISMATCH: 'mismatch',
  LOCKED: 'locked',
  NOT_FOUND: 'not-found',
};

const buildMismatchMessage = (remainingAttemptCount) =>
  Number.isInteger(remainingAttemptCount)
    ? `${MISMATCH_MESSAGE} You have ${remainingAttemptCount} attempt${
        remainingAttemptCount === 1 ? '' : 's'
      } left.`
    : MISMATCH_MESSAGE;

const DOBVerification = ({ workflowId, onVerified, onNotFound }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrorMessage('');
  };

  const handleVerify = async () => {
    if (isVerifying) return;

    if (!selectedDate) {
      setErrorMessage(MISSING_INPUT_MESSAGE);
      return;
    }

    setIsVerifying(true);
    const { data: verificationResponse } = await verifyDateOfBirth(
      workflowId,
      selectedDate.format(DATE_OF_BIRTH_PATTERN)
    );
    const verificationStatus = verificationResponse?.status;

    if (
      verificationStatus === VERIFICATION_RESULT.VERIFIED &&
      verificationResponse.accessToken &&
      verificationResponse.data
    ) {
      savePatientAccessToken(workflowId, verificationResponse.accessToken);
      onVerified(verificationResponse.data);
      return;
    }

    setIsVerifying(false);

    if (verificationStatus === VERIFICATION_RESULT.MISMATCH) {
      setErrorMessage(
        buildMismatchMessage(verificationResponse.remainingAttemptCount)
      );
      return;
    }

    if (verificationStatus === VERIFICATION_RESULT.LOCKED) {
      setIsLocked(true);
      setErrorMessage(LOCKED_MESSAGE);
      return;
    }

    if (verificationStatus === VERIFICATION_RESULT.NOT_FOUND && onNotFound) {
      onNotFound();
      return;
    }

    setErrorMessage(UNAVAILABLE_MESSAGE);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && selectedDate) {
      handleVerify();
    }
  };

  return (
    <>
      <BrandHeader />
      <PageShell>
        <PageHead>
          <PageTitle>
            For your privacy, please verify your date of birth
          </PageTitle>
          <PageSubtitle>
            We ask for this before showing your appointment and payment details.
          </PageSubtitle>
        </PageHead>

        <Card>
          <CardBody>
            <Field onKeyDown={handleKeyDown}>
              <FieldLabel htmlFor="date-of-birth">
                <RequiredMark>*</RequiredMark>
                Date Of Birth
              </FieldLabel>
              <DateInput
                id="date-of-birth"
                placeholder="DD/MM/YYYY"
                format={TIME_FORMAT.TABLE_TIME}
                value={selectedDate}
                onChange={handleDateChange}
                $hasError={Boolean(errorMessage)}
                disabled={isLocked || isVerifying}
              />
              {errorMessage && <InlineAlert>{errorMessage}</InlineAlert>}
            </Field>
          </CardBody>
        </Card>

        <ContactHelpText />
      </PageShell>

      <ActionBar
        nextLabel="Verify"
        onNext={handleVerify}
        isNextDisabled={isLocked}
        isLoading={isVerifying}
      />
    </>
  );
};

export default DOBVerification;
